import React, { useState, useEffect } from 'react';
import { Camera, Save, Upload, Mail, Phone, Globe, MapPin, User, Landmark, ShieldCheck, FileCheck2, X } from 'lucide-react';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2c/navBar/navBar';
import '../../../css/custom-scrollbar.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import { Base_Url, userdata, updateprofile, Url_For_View_Document } from '../../../config/config';
import { useNavigate } from 'react-router-dom';

const UserProfileEdit = () => {
    const [hasExistingAccount, setHasExistingAccount] = useState(false);
    const [originalAccountNo, setOriginalAccountNo] = useState('');
    const [hasExistingAccountHolder, setHasExistingAccountHolder] = useState(false);
    const [originalAccountHolder, setOriginalAccountHolder] = useState('');
    const [hasExistingBankName, setHasExistingBankName] = useState(false);
    const [originalBankName, setOriginalBankName] = useState('');
    const [hasExistingIfscCode, setHasExistingIfscCode] = useState(false);
    const [originalIfscCode, setOriginalIfscCode] = useState('');
    const { isDarkMode, isToggleSidebar } = useMyContext();
    const [cancelCheckFile, setCancelCheckFile] = useState(null);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    const [isLoading, isSetLoading] = useState(false);
    const navigate = useNavigate();

    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [selectedProfileImage, setSelectedProfileImage] = useState(null);

    const [userData, setUserData] = useState({
        id: cusid,
        company_name: "test",
        name: '',
        bio: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        account_holder: '',
        bank_name: '',
        account_no: '',
        ifsc_code: ''
    });

    const handleCancelCheckUpload = (event) => {
        const file = event.target.files[0];
        setCancelCheckFile(file);
    };

    const handleProfileImage = (event) => {
        const file = event.target.files[0];
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        setSelectedProfileImage(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleCancelCheckRemove = () => {
        setCancelCheckFile(null);
    };

    const generateInitialsAvatar = (name) => {
        if (!name) return null;
        const initials = name.charAt(0).toUpperCase() + name.charAt(1).toUpperCase();
        return (
            <div className={`rounded-full flex items-center justify-center font-bold w-full h-full text-3xl ${
                isDarkMode
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                    : 'bg-gray-800 text-white'
            }`}>
                {initials}
            </div>
        );
    };

    useEffect(() => {
        fetchUserData();
    }, [cusid, token]);

    const fetchUserData = async () => {
        const url = Base_Url + userdata;
        if (cusid) {
            try {
                const response = await fetch(url + "/" + cusid, {
                    method: 'GET',
                    headers: {
                        'token': token,
                        'cus_id': cusid,
                        'Content-Type': 'application/json',
                    },
                });

                const result = await response.json();
                if (result.status === "SUCCESS") {
                    const { data } = result;
                    if (data.account_no) {
                        setHasExistingAccount(true);
                        setOriginalAccountNo(data.account_no);
                    }
                    if (data.account_holder_name) {
                        setHasExistingAccountHolder(true);
                        setOriginalAccountHolder(data.account_holder_name);
                    }
                    if (data.bank_name) {
                        setHasExistingBankName(true);
                        setOriginalBankName(data.bank_name);
                    }
                    if (data.ifsc_code) {
                        setHasExistingIfscCode(true);
                        setOriginalIfscCode(data.ifsc_code);
                    }

                    setUserData({
                        id: cusid,
                        name: data.name || '',
                        company_name: data.company_name || '',
                        bio: data.bio,
                        email: data.email || '',
                        phone: data.phone || '',
                        website: data.website || '',
                        address: data.address || '',
                        account_holder: data.account_holder_name,
                        bank_name: data.bank_name,
                        account_no: data.account_no || '',
                        ifsc_code: data.ifsc_code
                    });
                    if (data.profile_image) {
                        const profileWithUrl = Url_For_View_Document + data.profile_image
                        setProfileImagePreview(profileWithUrl);
                    }
                    else {
                        setProfileImagePreview(data.profile_image);
                    }
                } else {
                    toast.error('Failed to fetch user data');
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                toast.error('Something went wrong. Please try again.');
            }
        }
    };

    const handleSubmit = async () => {
        const formData = new FormData();
        if (hasExistingAccount) {
            formData.append('account_no', originalAccountNo);
        } else {
            formData.append('account_no', userData.account_no);
        }

        if (hasExistingAccountHolder) {
            formData.append('account_holder', originalAccountHolder);
        } else {
            formData.append('account_holder', userData.account_holder);
        }

        if (hasExistingBankName) {
            formData.append('bank_name', originalBankName);
        } else {
            formData.append('bank_name', userData.bank_name);
        }

        if (hasExistingIfscCode) {
            formData.append('ifsc_code', originalIfscCode);
        } else {
            formData.append('ifsc_code', userData.ifsc_code);
        }

        for (const key in userData) {
            if (!['account_no', 'account_holder', 'bank_name', 'ifsc_code'].includes(key)) {
                formData.append(key, userData[key]);
            }
        }

        if (selectedProfileImage) {
            formData.append('profile_image', selectedProfileImage);
        }

        if (cancelCheckFile) {
            formData.append('cancel_check', cancelCheckFile);
        }

        try {
            isSetLoading(true);
            const response = await axios.post(`${Base_Url}${updateprofile}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'token': token,
                    'cus_id': cusid,
                },
            });
            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                isSetLoading(false);
                setTimeout(() => {
                    navigate("/dashboard");
                }, 1000)
            } else {
                toast.error(response.data.message.ifsc_code);
                toast.error(response.data.message.profile_image);
                toast.error(response.data.message.ifsc_code);
                toast.error(response.data.message.account_no);
                toast.error(response.data.message.address);
                toast.error(response.data.message.website);
                toast.error(response.data.message.cancel_check);
                isSetLoading(false);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Something went wrong. Please try again.');
            isSetLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        if (id === 'phone') {
            const phoneRegex = /^[0-9]{10}$/;
            if (!phoneRegex.test(value)) {
                return;
            }
        }
        if (
            (id === 'account_no' && hasExistingAccount) ||
            (id === 'account_holder' && hasExistingAccountHolder) ||
            (id === 'bank_name' && hasExistingBankName) ||
            (id === 'ifsc_code' && hasExistingIfscCode)
        ) {
            return;
        }
        setUserData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    // ✅ Premium input styling
    const fieldBase = `w-full px-3 py-2 text-[10px] rounded-xl border transition-all duration-300 outline-none`;
    const fieldActive = isDarkMode
        ? `bg-white/[0.03] border-white/10 text-white placeholder-gray-500 hover:border-white/20 focus:border-indigo-500/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-indigo-500/20 focus:shadow-[0_0_15px_rgba(99,102,241,0.15)]`
        : `bg-white border-gray-200 text-gray-800 focus:border-gray-400 focus:ring-2 focus:ring-gray-400/20`;
    const fieldLocked = isDarkMode
        ? `bg-gray-800/40 border-gray-800 text-gray-500 cursor-not-allowed`
        : `bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed`;

    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

    // Section wrapper
    const sectionClass = `rounded-2xl border p-3 transition-all duration-300 ${
        isDarkMode
            ? 'bg-white/[0.02] border-white/[0.08] hover:border-white/[0.12]'
            : 'bg-gray-50 border-gray-200'
    }`;

    const sectionTitleClass = `text-[9px] font-bold uppercase tracking-[0.15em] ${
        isDarkMode ? 'text-indigo-300' : 'text-gray-600'
    }`;

    const labelClass = `block text-[9px] font-bold uppercase tracking-wider mb-1 ${
        isDarkMode ? 'text-gray-500' : 'text-gray-500'
    }`;

    return (
        <div className={`h-screen w-full flex flex-col overflow-hidden transition-colors duration-300 ${
            isDarkMode ? 'dark bg-[#0B1120]' : 'bg-gray-50'
        }`}>
            {/* Premium ambient glows */}
            {isDarkMode && (
                <>
                    <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
                    <div className="pointer-events-none absolute bottom-0 -right-32 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
                </>
            )}

            <NavBar />
            <div className="flex flex-1 overflow-hidden relative z-10">
                {isToggleSidebar && (
                    <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
                        <Sidebar />
                    </div>
                )}

                <div className={`flex-1 min-w-0 min-h-0 flex flex-col p-3 md:p-4 transition-colors duration-300 ${
                    isDarkMode ? 'bg-[#0B1120]' : 'bg-gray-200'
                }`}>
                    <div className={`flex-1 min-h-0 flex flex-col rounded-2xl overflow-hidden border transition-all duration-300 ${
                        isDarkMode
                            ? 'bg-gradient-to-br from-[#111827] to-[#0F172A] border-gray-800 shadow-[0_0_40px_rgba(0,0,0,0.5)]'
                            : 'bg-white border-gray-200 shadow-sm'
                    }`}>

                        {/* Premium Header */}
                        <div className={`shrink-0 flex items-center justify-between px-4 py-3 border-b transition-colors duration-300 ${
                            isDarkMode
                                ? 'bg-gradient-to-r from-indigo-500/[0.08] via-purple-500/[0.05] to-transparent border-white/[0.06]'
                                : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex items-center gap-2.5">
                                <div className={`p-1.5 rounded-lg ${
                                    isDarkMode
                                        ? 'bg-gradient-to-br from-rose-500/20 to-red-500/10 border border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.2)]'
                                        : 'bg-red-50 border border-red-200'
                                }`}>
                                    <User size={12} className={isDarkMode ? 'text-rose-400' : 'text-red-500'} />
                                </div>
                                <div>
                                    <h1 className={`text-sm font-bold leading-none transition-colors duration-300 ${
                                        isDarkMode ? 'text-white' : 'text-gray-800'
                                    }`}>Edit Profile</h1>
                                    <p className={`text-[9px] mt-0.5 transition-colors duration-300 ${
                                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                    }`}>Manage your personal & payout details</p>
                                </div>
                            </div>
                            <button
                                className={`group relative flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-bold transition-all duration-300 overflow-hidden ${
                                    isDarkMode
                                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-[0_0_25px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.7)] hover:scale-[1.03]'
                                        : 'bg-red-600 text-white hover:opacity-85'
                                }`}
                                onClick={handleSubmit}
                            >
                                {isDarkMode && !isLoading && (
                                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                                )}
                                {isLoading ? <Spinner /> : (
                                    <>
                                        <Save size={12} className="relative z-10" />
                                        <span className="relative z-10">Save</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3 p-3 overflow-hidden">

                            {/* Left: Avatar + Bio */}
                            <div className="lg:w-64 shrink-0 flex flex-col gap-3">
                                <div className={`flex flex-col items-center justify-center gap-2.5 rounded-2xl border p-4 transition-all duration-300 ${
                                    isDarkMode
                                        ? 'bg-white/[0.02] border-white/[0.08]'
                                        : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <div className="relative w-24 h-24 md:w-28 md:h-28">
                                        {profileImagePreview ? (
                                            <img
                                                src={profileImagePreview}
                                                alt="Profile"
                                                className={`w-full h-full object-cover rounded-full border-2 shadow-lg ${
                                                    isDarkMode
                                                        ? 'border-indigo-500/40 shadow-[0_0_25px_rgba(99,102,241,0.3)]'
                                                        : 'border-gray-200'
                                                }`}
                                            />
                                        ) : (
                                            <div className={`w-full h-full rounded-full overflow-hidden border-2 shadow-lg ${
                                                isDarkMode
                                                    ? 'border-indigo-500/40 shadow-[0_0_25px_rgba(99,102,241,0.3)]'
                                                    : 'border-gray-200'
                                            }`}>
                                                {generateInitialsAvatar(userData.name)}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => document.getElementById("profileImageInput").click()}
                                            className={`absolute bottom-0 right-0 p-2 rounded-full shadow-md transition-all duration-300 hover:scale-110 ${
                                                isDarkMode
                                                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:shadow-[0_0_20px_rgba(99,102,241,0.6)]'
                                                    : 'bg-gray-800 text-white hover:opacity-85'
                                            }`}
                                        >
                                            <Camera size={12} />
                                        </button>
                                        <input
                                            type="file"
                                            id="profileImageInput"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            style={{ display: 'none' }}
                                            onChange={handleProfileImage}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <p className={`text-[11px] font-bold truncate max-w-[10rem] ${
                                            isDarkMode ? 'text-white' : 'text-gray-800'
                                        }`}>{userData.name || 'Your Name'}</p>
                                        <p className={`text-[9px] truncate max-w-[10rem] ${
                                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                        }`}>{userData.email || 'your@email.com'}</p>
                                    </div>
                                    <div className={`w-full pt-2 border-t flex items-center justify-center gap-1.5 text-[9px] ${
                                        isDarkMode
                                            ? 'border-white/[0.08] text-gray-500'
                                            : 'border-gray-200 text-gray-400'
                                    }`}>
                                        <div className={`w-1.5 h-1.5 rounded-full ${
                                            isDarkMode ? 'bg-emerald-400 animate-pulse' : 'bg-gray-400'
                                        }`}></div>
                                        Auto-saved on submit
                                    </div>
                                </div>

                                <div className={`rounded-2xl border p-3 flex flex-col gap-1.5 flex-1 min-h-0 transition-all duration-300 ${
                                    isDarkMode
                                        ? 'bg-white/[0.02] border-white/[0.08]'
                                        : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <label htmlFor="bio" className={labelClass}>Bio</label>
                                    <textarea
                                        id="bio"
                                        placeholder="Tell us about yourself..."
                                        value={userData.bio}
                                        onChange={handleInputChange}
                                        className={`${fieldBase} ${fieldActive} resize-none flex-1`}
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>

                            {/* Right: fields */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
                                <div className="flex flex-col gap-3">

                                    {/* Public info */}
                                    <div className={sectionClass}>
                                        <div className="flex items-center gap-2 mb-2.5">
                                            <div className={`p-1 rounded-md ${
                                                isDarkMode
                                                    ? 'bg-sky-500/10 border border-sky-500/30'
                                                    : 'bg-blue-50'
                                            }`}>
                                                <User size={11} className={isDarkMode ? 'text-sky-400' : 'text-blue-600'} />
                                            </div>
                                            <h2 className={sectionTitleClass}>Public Information</h2>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                            <div>
                                                <label htmlFor="name" className={labelClass}>Full Name *</label>
                                                <input
                                                    id="name"
                                                    type="text"
                                                    placeholder="Enter your full name"
                                                    value={userData.name}
                                                    onChange={handleInputChange}
                                                    className={`${fieldBase} ${fieldActive}`}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="username" className={labelClass}>Username</label>
                                                <input
                                                    id="username"
                                                    type="text"
                                                    placeholder="Choose a username"
                                                    value={userData.company_name}
                                                    onChange={handleInputChange}
                                                    className={`${fieldBase} ${fieldActive}`}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Private info */}
                                    <div className={sectionClass}>
                                        <div className="flex items-center gap-2 mb-2.5">
                                            <div className={`p-1 rounded-md ${
                                                isDarkMode
                                                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                                                    : 'bg-green-50'
                                            }`}>
                                                <ShieldCheck size={11} className={isDarkMode ? 'text-emerald-400' : 'text-green-600'} />
                                            </div>
                                            <h2 className={sectionTitleClass}>Private Information</h2>
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                                            <div>
                                                <label htmlFor="email" className={`${labelClass} flex items-center gap-1`}>
                                                    <Mail size={10} className={isDarkMode ? 'text-rose-400' : 'text-gray-500'} /> Email
                                                </label>
                                                <input id="email" type="email" disabled value={userData.email} className={`${fieldBase} ${fieldLocked}`} />
                                            </div>
                                            <div>
                                                <label htmlFor="phone" className={`${labelClass} flex items-center gap-1`}>
                                                    <Phone size={10} className={isDarkMode ? 'text-emerald-400' : 'text-gray-500'} /> Phone
                                                </label>
                                                <input id="phone" type="tel" disabled value={userData.phone} className={`${fieldBase} ${fieldLocked}`} />
                                            </div>
                                            <div>
                                                <label htmlFor="website" className={`${labelClass} flex items-center gap-1`}>
                                                    <Globe size={10} className={isDarkMode ? 'text-cyan-400' : 'text-gray-500'} /> Website
                                                </label>
                                                <input id="website" type="url" placeholder="https://..." value={userData.website} onChange={handleInputChange} className={`${fieldBase} ${fieldActive}`} />
                                            </div>
                                            <div>
                                                <label htmlFor="UserId" className={`${labelClass} flex items-center gap-1`}>
                                                    User ID
                                                </label>
                                                <input id="UserId" type="text" disabled value={userData.id} className={`${fieldBase} ${fieldLocked}`} />
                                            </div>
                                            <div className="col-span-2 lg:col-span-4">
                                                <label htmlFor="address" className={`${labelClass} flex items-center gap-1`}>
                                                    <MapPin size={10} className={isDarkMode ? 'text-amber-400' : 'text-gray-500'} /> Address
                                                </label>
                                                <textarea
                                                    id="address"
                                                    placeholder="Enter your complete address"
                                                    value={userData.address}
                                                    onChange={handleInputChange}
                                                    className={`${fieldBase} ${fieldActive} resize-none`}
                                                    rows="2"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bank details */}
                                    <div className={sectionClass}>
                                        <div className="flex items-center gap-2 mb-2.5">
                                            <div className={`p-1 rounded-md ${
                                                isDarkMode
                                                    ? 'bg-amber-500/10 border border-amber-500/30'
                                                    : 'bg-amber-50'
                                            }`}>
                                                <Landmark size={11} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
                                            </div>
                                            <h2 className={sectionTitleClass}>Bank Details</h2>
                                        </div>

                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                                            <div className={`${hasExistingAccountHolder ? 'opacity-70' : ''}`}>
                                                <label htmlFor="account_holder" className={labelClass}>
                                                    Account Holder {hasExistingAccountHolder && <span className={`text-[8px] px-1.5 rounded ml-1 ${
                                                        isDarkMode
                                                            ? 'bg-indigo-500/20 text-indigo-300'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }`}>Locked</span>}
                                                </label>
                                                <input
                                                    id="account_holder"
                                                    type="text"
                                                    placeholder="Account holder name"
                                                    value={hasExistingAccountHolder ? originalAccountHolder : userData.account_holder}
                                                    onChange={handleInputChange}
                                                    onInput={(e) => { e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, ''); }}
                                                    disabled={hasExistingAccountHolder}
                                                    className={`${fieldBase} ${hasExistingAccountHolder ? fieldLocked : fieldActive}`}
                                                />
                                            </div>

                                            <div className={`${hasExistingBankName ? 'opacity-70' : ''}`}>
                                                <label htmlFor="bank_name" className={labelClass}>
                                                    Bank Name {hasExistingBankName && <span className={`text-[8px] px-1.5 rounded ml-1 ${
                                                        isDarkMode
                                                            ? 'bg-indigo-500/20 text-indigo-300'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }`}>Locked</span>}
                                                </label>
                                                <input
                                                    id="bank_name"
                                                    type="text"
                                                    placeholder="Bank name"
                                                    value={hasExistingBankName ? originalBankName : userData.bank_name}
                                                    onChange={handleInputChange}
                                                    disabled={hasExistingBankName}
                                                    onInput={(e) => { e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, ''); }}
                                                    className={`${fieldBase} ${hasExistingBankName ? fieldLocked : fieldActive}`}
                                                />
                                            </div>

                                            <div className={`${hasExistingAccount ? 'opacity-70' : ''}`}>
                                                <label htmlFor="account_no" className={labelClass}>
                                                    Account No. {hasExistingAccount && <span className={`text-[8px] px-1.5 rounded ml-1 ${
                                                        isDarkMode
                                                            ? 'bg-indigo-500/20 text-indigo-300'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }`}>Locked</span>}
                                                </label>
                                                <input
                                                    id="account_no"
                                                    type="text"
                                                    placeholder="Account number"
                                                    value={hasExistingAccount ? originalAccountNo : userData.account_no}
                                                    maxLength={16}
                                                    onChange={handleInputChange}
                                                    onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ''); }}
                                                    disabled={hasExistingAccount}
                                                    className={`${fieldBase} ${hasExistingAccount ? fieldLocked : fieldActive}`}
                                                />
                                            </div>

                                            <div className={`${hasExistingIfscCode ? 'opacity-70' : ''}`}>
                                                <label htmlFor="ifsc_code" className={labelClass}>
                                                    IFSC Code {hasExistingIfscCode && <span className={`text-[8px] px-1.5 rounded ml-1 ${
                                                        isDarkMode
                                                            ? 'bg-indigo-500/20 text-indigo-300'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }`}>Locked</span>}
                                                </label>
                                                <input
                                                    id="ifsc_code"
                                                    type="text"
                                                    placeholder="IFSC code"
                                                    value={hasExistingIfscCode ? originalIfscCode : userData.ifsc_code}
                                                    onChange={handleInputChange}
                                                    disabled={hasExistingIfscCode}
                                                    className={`${fieldBase} ${hasExistingIfscCode ? fieldLocked : fieldActive}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Cancel check upload */}
                                        <div className={`mt-3 pt-3 border-t border-dashed flex items-center gap-2 flex-wrap ${
                                            isDarkMode ? 'border-white/[0.08]' : 'border-gray-200'
                                        }`}>
                                            <FileCheck2 size={12} className={isDarkMode ? 'text-indigo-400 shrink-0' : 'text-gray-400 shrink-0'} />
                                            <label className={`text-[9px] font-bold uppercase tracking-wider shrink-0 ${
                                                isDarkMode ? 'text-gray-500' : 'text-gray-500'
                                            }`}>
                                                Cancelled Check
                                            </label>

                                            <input
                                                type="file"
                                                id="cancel-check-upload"
                                                className="hidden"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={handleCancelCheckUpload}
                                            />
                                            <label
                                                htmlFor="cancel-check-upload"
                                                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg cursor-pointer transition-all duration-300 text-[9px] font-bold shrink-0 ${
                                                    isDarkMode
                                                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                                        : 'bg-gray-800 text-white hover:opacity-85'
                                                }`}
                                            >
                                                <Upload size={10} />
                                                <span>Choose File</span>
                                            </label>

                                            {cancelCheckFile && (
                                                <div className={`flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg text-[9px] border ${
                                                    isDarkMode
                                                        ? 'bg-emerald-500/10 border-emerald-500/30'
                                                        : 'bg-gray-100 border-gray-200'
                                                }`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${
                                                        isDarkMode ? 'bg-emerald-400 animate-pulse' : 'bg-green-500'
                                                    }`}></div>
                                                    <span className={`truncate max-w-[6rem] font-medium ${
                                                        isDarkMode ? 'text-emerald-300' : 'text-gray-700'
                                                    }`}>{cancelCheckFile.name}</span>
                                                    <button
                                                        onClick={handleCancelCheckRemove}
                                                        className={`p-0.5 rounded transition-colors ${
                                                            isDarkMode
                                                                ? 'text-rose-400 hover:text-rose-300 hover:bg-rose-500/10'
                                                                : 'text-red-500 hover:text-red-700'
                                                        }`}
                                                        type="button"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfileEdit;